import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../website/components/WebsiteLayout';
import Reveal from '../website/components/Reveal';
import { useLanguage } from '../../hooks/useLanguage';

const Research: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? "Working on something where the substrate matters? Let's compare notes."
          : 'Werk je aan iets waarbij de AI-laag ertoe doet? Laten we ervaringen uitwisselen.'}
      </h2>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact">
          {en ? 'Contact us' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );

  return (
    <WebsiteLayout customCta={customCta}>
      <Helmet>
        <title>Research · Thoughtful Oasis</title>
        <meta
          name="description"
          content="The foundations our system is built on. Papers, plain-language summaries, and presentations."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/research" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Thoughtful Oasis" />
        <meta property="og:title" content="Research · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="Three papers: a formal framework, an empirical validation, and a constructive demonstration on the hardest retrieval benchmark."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/research" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta property="og:locale" content={en ? 'en_GB' : 'nl_NL'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Research · Thoughtful Oasis" />
        <meta
          name="twitter:description"
          content="The foundations our system is built on. Papers, plain-language summaries, and presentations."
        />
        <meta name="twitter:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
      </Helmet>

      <header className="hero hero--sub">
        <div className="hero__inner">
          <span className="label">{en ? 'Research' : 'Onderzoek'}</span>
          <h1 style={{ marginTop: 20 }}>
            {en ? 'A new substrate for AI.' : 'Een nieuwe AI-laag.'}
          </h1>
          <p className="hero__sub">
            {en
              ? 'Interpretable by design, efficient by construction.'
              : 'Interpreteerbaar en efficiënt van opzet.'}
          </p>
        </div>
      </header>

      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">Papers</span>
            <h2 style={{ marginTop: 18 }}>Papers</h2>
          </Reveal>

          <div className="paper-list">
            <Reveal className="paper-list__item">
              <span className="paper-list__year">2026</span>
              <div>
                <p className="paper-list__title">
                  {en
                    ? 'Domain-Property Alignment in Single-Vector Retrieval'
                    : 'Domein-eigenschap-alignment in single-vector retrieval'}
                </p>
                <p className="paper-list__desc">
                  {en
                    ? "Weller et al.'s sphere-packing bound on embedding dimension is correct but captures capacity, not the constraint governing real retrieval. What sets the dimension needed is alignment between the encoder's axes and the domain's property vocabulary. On the LIMIT benchmark, a property basis at d=1848 achieves perfect recall; an SVD truncation to d=2 already beats every dense retriever. An interpretable aligned basis additionally turns Boolean compounds into closed-form linear arithmetic."
                    : 'De sphere-packing-grens van Weller et al. op embedding-dimensie klopt, maar vangt capaciteit — niet de beperking die echte retrieval beheerst. Wat de benodigde dimensie bepaalt is alignment tussen de assen van de encoder en het eigenschap-vocabulaire van het domein. Op de LIMIT-benchmark haalt een eigenschap-basis op d=1848 perfecte recall; een SVD-truncatie naar d=2 verslaat al elke dense retriever. Een interpreteerbare uitgelijnde basis maakt bovendien Booleaanse samenstellingen tot gesloten-vorm lineaire rekenkunde.'}
                </p>
                <p className="paper-list__authors">Kaspar Rothenfusser &amp; Bekk Blando</p>
              </div>
              <div className="paper-list__actions">
                <Link className="paper-list__link" to="/research/limit">
                  {en ? 'Plain language' : 'In gewone taal'}
                </Link>
                <a
                  className="paper-list__link paper-list__link--quiet"
                  href="/limit.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Paper
                </a>
              </div>
            </Reveal>

            <Reveal className="paper-list__item">
              <span className="paper-list__year">2025</span>
              <div>
                <p className="paper-list__title">
                  {en
                    ? 'Vector Ontologies as an LLM World View Extraction Method'
                    : 'Vector-ontologieën als methode om het wereldbeeld van een LLM uit te lezen'}
                </p>
                <p className="paper-list__desc">
                  {en
                    ? 'First empirical validation. We constructed an eight-axis vector ontology of musical genres and asked GPT-4o-mini to populate it, with no fine-tuning. Across 47 differently-phrased queries about the same genre, the LLM landed in 12.5 unique cells out of 1.7 million: stable, reproducible, and accurate against ground truth from 15 million real songs.'
                    : 'Eerste empirische validatie. We bouwden een vector-ontologie van muziekgenres met acht assen en vroegen GPT-4o-mini om die te vullen, zonder fine-tuning. Over 47 anders geformuleerde vragen over hetzelfde genre kwam het LLM uit in 12,5 unieke cellen op 1,7 miljoen: stabiel, reproduceerbaar, en accuraat ten opzichte van de grondwaarheid uit 15 miljoen echte nummers.'}
                </p>
                <p className="paper-list__authors">Kaspar Rothenfusser &amp; Bekk Blando</p>
              </div>
              <div className="paper-list__actions">
                <Link className="paper-list__link" to="/research/worldview">
                  {en ? 'Plain language' : 'In gewone taal'}
                </Link>
                <a
                  className="paper-list__link paper-list__link--quiet"
                  href="https://arxiv.org/abs/2506.13252"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Paper
                </a>
              </div>
            </Reveal>

            <Reveal className="paper-list__item">
              <span className="paper-list__year">2025</span>
              <div>
                <p className="paper-list__title">
                  {en
                    ? 'To Be or Not To Be: Vector ontologies as a truly formal ontological framework'
                    : 'To Be or Not To Be: vector-ontologieën als werkelijk formeel ontologisch kader'}
                </p>
                <p className="paper-list__desc">
                  {en
                    ? 'A theoretical case for vector spaces as the genuine home of formal ontology. Returns to Husserl\'s strict definition of "formal," shows that classical ontologies fail it, and presents vector ontologies — with named, interpretable axes — as the shared framework between human and machine ontological reasoning.'
                    : 'Een theoretische onderbouwing voor vectorruimtes als de echte plek voor formele ontologie. Keert terug naar Husserls strikte definitie van "formeel", laat zien dat klassieke ontologieën daar niet aan voldoen, en presenteert vector-ontologieën — met benoemde, interpreteerbare assen — als het gedeelde kader tussen menselijk en machinaal ontologisch redeneren.'}
                </p>
                <p className="paper-list__authors">
                  {en ? 'Kaspar Rothenfusser · sole author' : 'Kaspar Rothenfusser · enige auteur'}
                </p>
              </div>
              <div className="paper-list__actions">
                <Link className="paper-list__link" to="/research/vector-ontologies">
                  {en ? 'Plain language' : 'In gewone taal'}
                </Link>
                <a
                  className="paper-list__link paper-list__link--quiet"
                  href="https://arxiv.org/abs/2505.14940"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Paper
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Presentations' : 'Presentaties'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en
                ? 'Where we present the work in person.'
                : 'Waar we het werk live presenteren.'}
            </h2>
          </Reveal>

          <div className="paper-list">
            <Reveal className="paper-list__item">
              <span className="paper-list__year">2026</span>
              <div>
                <p className="paper-list__title">
                  {en
                    ? 'ICT.OPEN — vector ontologies as the reasoning layer for compliance-grade AI'
                    : 'ICT.OPEN — vector-ontologieën als redeneerlaag voor compliance-waardige AI'}
                </p>
                <p className="paper-list__desc">
                  {en
                    ? 'A talk on what changes when AI is built on a substrate whose dimensions are named. Slides available on request.'
                    : 'Een talk over wat er verandert als AI gebouwd wordt op een laag waarvan de assen benoemd zijn. Slides op aanvraag beschikbaar.'}
                </p>
              </div>
              <div className="paper-list__actions">
                <a
                  className="paper-list__link"
                  href="mailto:kaspar@thoughtful-oasis.com?subject=ICT.OPEN%202026%20slides"
                >
                  Slides
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default Research;
